import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import MuiListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Skeleton from '@mui/material/Skeleton';
import EllipsisTitle from 'components/editor/SidePanel/DocumentSettings/CurrencyAutocomplete/EllipsisMenuItem';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

function Container({ children }: { children: React.ReactNode }) {
  return (
    <Stack direction="column" width="100%" gap={2} overflow="hidden">
      {children}
    </Stack>
  );
}

function Navigation({ children }: { children: React.ReactNode }) {
  return <Box sx={{ minWidth: 0, overflow: 'hidden' }}>{children}</Box>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack direction="column" gap={0.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Typography variant="subtitle2" fontWeight="bold" color="text.primary" noWrap>
        {title}
      </Typography>
      <List sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>{children}</List>
    </Stack>
  );
}

type LoadedItemProps = {
  children: React.ReactNode;
  onClick?: () => void;
  folderName?: string;
};
type LoadingItemProps = {
  isLoading: boolean;
};

function LibraryListItem({ children, onClick, isLoading }: LoadedItemProps & LoadingItemProps) {
  return (
    <MuiListItem
      data-testid="snippet-list-item"
      disablePadding
      sx={{
        '& .MuiListItemSecondaryAction-root': { visibility: 'hidden' },
        '&:hover .MuiListItemSecondaryAction-root': { visibility: 'visible' },
        transition: 'visibility 0.2s',
      }}
    >
      <ListItemButton
        data-testid="snippet-list-item-button"
        sx={{
          pointerEvents: onClick ? 'auto' : 'none',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        onClick={onClick}
        dense
      >
        {isLoading ? <Skeleton variant="text" width="100%" height="100%" /> : children}
      </ListItemButton>
    </MuiListItem>
  );
}

function AssetListItem({ children, onClick, folderName }: LoadedItemProps);
function AssetListItem({ isLoading }: LoadingItemProps);
function AssetListItem({ children = '', onClick, isLoading, folderName }: Partial<LoadedItemProps & LoadingItemProps>) {
  return (
    <LibraryListItem isLoading={Boolean(isLoading)} onClick={onClick}>
      <Stack
        data-testid="snippet-item-content"
        direction="column"
        width="100%"
        sx={(theme) => ({
          '.MuiTypography-root': {
            ...theme.typography.caption,
            color: 'text.primary',
          },
        })}
      >
        <EllipsisTitle text={String(children)} />
        {folderName && (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <FolderRoundedIcon sx={{ color: 'text.secondary', fontSize: '0.75rem' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {folderName}
            </Typography>
          </Stack>
        )}
      </Stack>
    </LibraryListItem>
  );
}

function FolderListItem({ children, onClick }: LoadedItemProps);
function FolderListItem({ isLoading }: LoadingItemProps);
function FolderListItem({ children = '', onClick, isLoading }: Partial<LoadedItemProps & LoadingItemProps>) {
  return (
    <LibraryListItem isLoading={Boolean(isLoading)} onClick={onClick}>
      <Stack
        data-testid="folder-item-content"
        direction="row"
        alignItems="center"
        width="100%"
        gap={0.5}
        sx={(theme) => ({
          '.MuiTypography-root': {
            ...theme.typography.subtitle2,
            color: 'text.primary',
            flexGrow: 1,
          },
        })}
      >
        <FolderRoundedIcon sx={{ color: 'action.active', flexShrink: 0, flexGrow: 0 }} fontSize="small" />
        <EllipsisTitle text={String(children)} />
        <ChevronRightRoundedIcon sx={{ color: 'action.active', flexShrink: 0, flexGrow: 0 }} />
      </Stack>
    </LibraryListItem>
  );
}

export const LibraryLayout = {
  Container,
  Navigation,
  Section,
  AssetListItem,
  FolderListItem,
};
